// <sm-pdp-offer-form>: the subscribe/one-time buy box (HANDOFF 7.1).
// Plan switching itself needs no JS (CSS :has() in sm-offer-card, see sm-pdp-offer.css);
// this only submits the form via the cart API and keeps the CTA button and the sticky
// bar / plan dropdown (sm:plan:changed) in sync with whichever plan is checked.
import { cartAdd, emit } from './sm-core.js';

class SmPdpOfferForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.ctaBtn = this.querySelector('[data-cta-label]');
    this.oneTimeBtn = this.querySelector('[data-one-time-toggle]');

    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onOneTimeToggle = this.onOneTimeToggle.bind(this);

    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('change', this.onChange);
    this.oneTimeBtn?.addEventListener('click', this.onOneTimeToggle);

    this.syncFromChecked();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form?.removeEventListener('change', this.onChange);
    this.oneTimeBtn?.removeEventListener('click', this.onOneTimeToggle);
  }

  onChange(e) {
    if (!e.target.classList?.contains('sm-offer-card__radio')) return;
    this.syncFromChecked();
  }

  syncFromChecked() {
    const checked = this.form.querySelector('.sm-offer-card__radio:checked');
    if (!checked) return;
    const card = checked.closest('.sm-offer-card');
    if (!card) return;
    const cta = card.dataset.planCta;
    if (this.ctaBtn && cta) this.ctaBtn.textContent = cta;
    emit('sm:plan:changed', {
      label: cta,
      price: Number(card.dataset.planPrice || 0),
      perDay: Number(card.dataset.planPerDay || 0),
      days: Number(card.dataset.planDays || 0),
      planId: card.dataset.planId,
    });
  }

  onOneTimeToggle() {
    const pressed = this.oneTimeBtn.getAttribute('aria-pressed') === 'true';
    const next = !pressed;
    this.oneTimeBtn.setAttribute('aria-pressed', String(next));
    const radios = [...this.form.querySelectorAll('.sm-offer-card__radio')];
    if (next) {
      this.lastChecked = radios.find((r) => r.checked) || null;
      radios.forEach((r) => { r.checked = false; });
      if (this.ctaBtn && this.ctaBtn.dataset.onetimeCta) {
        this.ctaBtn.textContent = this.ctaBtn.dataset.onetimeCta;
      }
      emit('sm:plan:changed', { label: this.ctaBtn?.dataset.onetimeCta, price: null, perDay: null, days: null, planId: null });
    } else if (this.lastChecked) {
      this.lastChecked.checked = true;
      this.syncFromChecked();
    } else if (radios[0]) {
      radios[0].checked = true;
      this.syncFromChecked();
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitting) return;
    this.submitting = true;
    this.ctaBtn?.setAttribute('aria-busy', 'true');
    try {
      const data = new FormData(this.form);
      const item = { id: data.get('id'), quantity: 1 };
      const sellingPlan = data.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;
      await cartAdd([item], ['sm-cart-drawer-offer']);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('sm-pdp-offer: add to cart failed', err);
    } finally {
      this.submitting = false;
      this.ctaBtn?.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('sm-pdp-offer-form')) customElements.define('sm-pdp-offer-form', SmPdpOfferForm);

// Ask smööts AI: static widget (HANDOFF 7.10), chips fill the input, no endpoint yet.
document.querySelectorAll('.sm-pdp-offer__ask-ai').forEach((widget) => {
  const input = widget.querySelector('[data-ask-ai-input]');
  // with the AI chat live (sm-ai-chat.js) chips ask directly; this only fills the input as a fallback
  widget.querySelectorAll('[data-ask-ai-chip]').forEach((chip) => {
    chip.addEventListener('click', () => {
      if (!input || widget.hasAttribute('data-ai-widget')) return;
      input.value = chip.dataset.askAiChip || '';
      input.focus();
    });
  });
  const tabs = widget.querySelectorAll('[data-ask-ai-tab]');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        t.classList.toggle('sm-pdp-offer__ask-ai-tab--active', t === tab);
      });
    });
  });
});

// Gallery "facts" thumbnail: jump to the supplement facts accordion.
document.querySelectorAll('.sm-pdp-offer__gallery-thumb--facts').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.getElementById('facts')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
});

// Gallery (David 29.9.2026): the big tile is a swipeable stage. Slide 1 is the welcome kit tile,
// every thumbnail gets a big slide; thumbs, arrows, swipe and the counter all stay in sync.
document.querySelectorAll('[data-pdp-gallery]').forEach((gal) => {
  const grid = gal.querySelector('.sm-pdp-offer__gallery-grid');
  const flash = grid?.querySelector('.sm-pdp-offer__gallery-flash');
  if (!grid || !flash || gal.dataset.ready) return;
  gal.dataset.ready = '1';
  // drop empty thumbnails (e.g. supplement facts without an image)
  let thumbs = [...grid.querySelectorAll('.sm-pdp-offer__gallery-thumb')].filter((t) => {
    const empty = !t.querySelector('img') && !t.querySelector('.sm-pdp-offer__gallery-stat');
    if (empty) t.remove();
    return !empty;
  });
  const stage = document.createElement('div');
  stage.className = 'sm-pdp-offer__stage';
  const track = document.createElement('div');
  track.className = 'sm-pdp-offer__stage-track';
  track.tabIndex = 0;
  flash.replaceWith(stage);
  stage.append(track);
  const slide0 = document.createElement('div');
  slide0.className = 'sm-pdp-offer__stage-slide';
  slide0.append(flash);
  track.append(slide0);
  thumbs.forEach((t) => {
    const s = document.createElement('div');
    s.className = 'sm-pdp-offer__stage-slide sm-pdp-offer__stage-slide--' + ([...t.classList].find((c) => c.includes('--'))?.split('--')[1] || 'image');
    s.innerHTML = t.innerHTML;
    s.querySelectorAll('img').forEach((img) => { img.sizes = '(min-width: 990px) 750px, 100vw'; img.loading = 'lazy'; });
    track.append(s);
  });
  // kit thumbnail is server-rendered (first in the grid); create it only if the markup is older
  let kitThumb = grid.querySelector('[data-kit-thumb]');
  if (!kitThumb) {
    const kitImg = flash.querySelector('.sm-pdp-offer__flash-gift img, img');
    kitThumb = document.createElement('button');
    kitThumb.type = 'button';
    kitThumb.className = 'sm-pdp-offer__gallery-thumb sm-pdp-offer__gallery-thumb--kit';
    kitThumb.setAttribute('aria-label', gal.dataset.labelKit || '');
    if (kitImg) { const c = kitImg.cloneNode(); c.sizes = '120px'; c.removeAttribute('fetchpriority'); kitThumb.append(c); }
    grid.insertBefore(kitThumb, thumbs[0] || null);
  }
  thumbs = thumbs.filter((t) => t !== kitThumb);
  thumbs = [kitThumb, ...thumbs];
  const slides = [...track.children];
  const mk = (dir, label, icon) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'sm-pdp-offer__stage-arrow sm-pdp-offer__stage-arrow--' + dir;
    b.setAttribute('aria-label', label || dir);
    b.innerHTML = icon;
    return b;
  };
  const chev = (d) => `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
  const prev = mk('prev', gal.dataset.labelPrev, chev('m15 18-6-6 6-6'));
  const next = mk('next', gal.dataset.labelNext, chev('m9 18 6-6-6-6'));
  const count = document.createElement('span');
  count.className = 'sm-mono sm-pdp-offer__stage-count';
  stage.append(prev, next, count);
  const idx = () => Math.round(track.scrollLeft / track.clientWidth);
  const go = (i) => track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, i)) * track.clientWidth, behavior: 'smooth' });
  const sync = () => {
    const i = idx();
    thumbs.forEach((t, n) => { t.classList.toggle('is-active', n === i); t.setAttribute('aria-current', String(n === i)); });
    count.textContent = `${i + 1} / ${slides.length}`;
    prev.disabled = i === 0;
    next.disabled = i === slides.length - 1;
  };
  thumbs.forEach((t, n) => t.addEventListener('click', (e) => { e.preventDefault(); e.stopImmediatePropagation(); go(n); }, true));
  prev.addEventListener('click', () => go(idx() - 1));
  next.addEventListener('click', () => go(idx() + 1));
  track.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') go(idx() + 1); if (e.key === 'ArrowLeft') go(idx() - 1); });
  let raf;
  track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }, { passive: true });
  sync();
});

// Switching plans: animate the open card closing and the clicked one opening (View Transitions where
// supported, fade-in otherwise) and keep the clicked card where the pointer is, so nothing jumps.
document.addEventListener('click', (e) => {
  const card = e.target.closest('.sm-pdp-offer .sm-offer-card');
  if (!card) return;
  const input = card.querySelector('input[type=radio]');
  if (!input || input.checked) return;
  e.preventDefault();
  const before = card.getBoundingClientRect().top;
  const cards = [...card.parentElement.querySelectorAll('.sm-offer-card')];
  cards.forEach((c, i) => { c.style.viewTransitionName = `sm-plan-${i}`; });
  const apply = () => {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const diff = card.getBoundingClientRect().top - before;
    if (Math.abs(diff) > 1) window.scrollBy({ top: diff, behavior: 'instant' });
  };
  const done = () => cards.forEach((c) => { c.style.viewTransitionName = ''; });
  if (document.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.startViewTransition(apply).finished.finally(done);
  } else { apply(); done(); }
});
